/**
 * deleteLocation — RPC contract for the deleteLocation callable, the only
 * path that destroys a location. `location.entity.ts` marks `delete`
 * `.requiresHandler('deleteLocation')`. The references that decide this
 * delete live in three other collections — an event's venue, a ticket type's
 * room override, a room's parent venue — and a Firestore rule can query none
 * of them, so the authorization is the callable itself.
 *
 * The call names the location, never the workspace: `orgId` is
 *
 * Fields justified:
 *   locationId — the location to delete; the path is
 *                workspaces/{orgId}/locations/{locationId}.
 * Excluded:
 *   a confirmation flag — acknowledgement is what the form renders, and a
 *           flag the caller sets to true guards nothing the caller could not
 *           set anyway; the census inside the transaction is the guard.
 *
 * Output: a discriminated union on `outcome`, because a refusal is the
 * answer the operator asked for — the counts standing in the way, rendered
 * with the remedy — not an exception. An unauthenticated or unpermitted
 * caller still throws `HttpsError`.
 *
 * Output fields justified:
 *   dependents — the census, on refusal only.
 * Excluded from the deleted branch:
 *   any cascade count — a location that survives its census is named by
 *                nothing, so the delete removes exactly one document and a
 *                count of it would always read 1.
 *
 * Organization context is explicit in the request. The server validates
 * membership or possession of the scoped access token independently.
 */
import { z } from "zod";
import { DependentsSchema } from "./dependents.const.ts";
import { Id } from "./workspace.entity.ts";

export const DeleteLocationInput = z
	.object({ orgId: Id, locationId: Id.describe("the location to delete") })
	.strict();
export type DeleteLocationInput = z.infer<typeof DeleteLocationInput>;

export const DeleteLocationOutput = z.discriminatedUnion("outcome", [
	z.object({ outcome: z.literal("deleted") }).strict(),
	z
		.object({
			outcome: z.literal("refused"),
			dependents: DependentsSchema.describe(
				"what still names the location, in counts — the refusal's whole substance",
			),
		})
		.strict(),
]);
export type DeleteLocationOutput = z.infer<typeof DeleteLocationOutput>;
